import { z } from 'zod'
import { asObjectType } from '@gqloom/zod'

export const ACCESS_SCHEDULE_MODES = ['TWENTY_FOUR_HOUR', 'BUSINESS_HOURS', 'CUSTOM'] as const
export const accessScheduleModeSchema = z.enum(ACCESS_SCHEDULE_MODES)
export type AccessScheduleMode = z.infer<typeof accessScheduleModeSchema>

export const SCHEDULE_DAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const
export const scheduleDaySchema = z.enum(SCHEDULE_DAYS)
export type ScheduleDay = z.infer<typeof scheduleDaySchema>

const timeOfDayRegex = /^([01]\d|2[0-3]):[0-5]\d$/
const isoDateRegex = /^\d{4}-\d{2}-\d{2}$/

export const accessTimeRangeSchema = z.object({
  start: z.string().regex(timeOfDayRegex),
  end: z.string().regex(timeOfDayRegex),
})
export type AccessTimeRange = z.infer<typeof accessTimeRangeSchema>

export const accessScheduleDaySchema = z.object({
  day: scheduleDaySchema,
  ranges: z.array(accessTimeRangeSchema),
})
export type AccessScheduleDay = z.infer<typeof accessScheduleDaySchema>

export const accessScheduleExceptionSchema = z.object({
  date: z.string().regex(isoDateRegex),
  label: z.string().optional(),
})
export type AccessScheduleException = z.infer<typeof accessScheduleExceptionSchema>

export const accessScheduleSchema = z.object({
  mode: accessScheduleModeSchema,
  sameTimeForAllDays: z.boolean(),
  days: z.array(accessScheduleDaySchema),
  exceptions: z.array(accessScheduleExceptionSchema),
})
export type AccessSchedule = z.infer<typeof accessScheduleSchema>

export const doorAccessDenialSourceSchema = z.enum(['ZONE_SCHEDULE', 'MEMBERSHIP_WINDOW'])
export type DoorAccessDenialSource = z.infer<typeof doorAccessDenialSourceSchema>

export const WHO_CAN_ENTER_MODES = ['SESSION_TYPES', 'ALL_MEMBERS'] as const
export const whoCanEnterModeSchema = z.enum(WHO_CAN_ENTER_MODES)
export type WhoCanEnterMode = z.infer<typeof whoCanEnterModeSchema>

export const whoCanEnterSchema = z.object({
  mode: whoCanEnterModeSchema,
  siteSessionTypeIds: z.array(z.string()),
})
export type WhoCanEnter = z.infer<typeof whoCanEnterSchema>

export const siteAccessZoneConfigSchema = z.object({
  accessSchedule: accessScheduleSchema.optional(),
  whoCanEnter: whoCanEnterSchema.optional(),
})
export type SiteAccessZoneConfig = z.infer<typeof siteAccessZoneConfigSchema>

export const siteAccessZoneSchema = z
  .object({
    siteAccessZoneId: z.string(),
    siteId: z.string(),
    name: z.string(),
    config: z.record(z.string(), z.unknown()).nullable().optional(),
    metadata: z.record(z.string(), z.unknown()).nullable().optional(),
    createdAt: z.date(),
    updatedAt: z.date(),
  })
  .register(asObjectType, {
    name: 'SiteAccessZone',
    description: 'A named access zone within a site (e.g. Main, Premium Area)',
  })

export type SiteAccessZone = z.infer<typeof siteAccessZoneSchema>

export const siteAccessZoneCreateSchema = z
  .object({
    siteId: z.string(),
    name: z.string().min(1).max(255),
    metadata: z.record(z.string(), z.unknown()).nullable().optional(),
  })
  .register(asObjectType, {
    name: 'SiteAccessZoneCreateInput',
    description: 'Input for creating an access zone',
  })

export type TSiteAccessZoneCreate = z.infer<typeof siteAccessZoneCreateSchema>

export const siteAccessZoneUpdateSchema = z
  .object({
    name: z.string().min(1).max(255).optional(),
    config: z.record(z.string(), z.unknown()).nullable().optional(),
    metadata: z.record(z.string(), z.unknown()).nullable().optional(),
  })
  .register(asObjectType, {
    name: 'SiteAccessZoneUpdateInput',
    description: 'Input for updating an access zone',
  })

export type TSiteAccessZoneUpdate = z.infer<typeof siteAccessZoneUpdateSchema>
